<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import AgGrid from '@/components/AgGrid.vue'
import type { ColumnDef } from '@/components/AgGrid.vue'
import type { GridApi, GridReadyEvent, CellValueChangedEvent } from 'ag-grid-community'
import type { GridData, TransientGridState } from '~/types/schedule'
import { columnSchema } from '~/types/schedule'
import { useAgGridAutocomplete, type AutocompleteItem } from '@/composables/useAgGridAutocomplete'
import type { AutocompleteFetchParams, AutocompleteSelectParams } from '@/types/autocomplete'
import { useProjectStore } from '@/stores/projectStore'
// Add panel-related imports
import type { Panel } from '@/types/projectdock'
import type { Tool } from '@/types/tools'
// Add parse function import
import { usePlantGrid } from '@/composables/tools/usePlantGrid'
import { usePanelData } from '@/composables/usePanelData'
import { usePanelConnections } from '@/composables/usePanelConnections'
import { useMitter } from '#imports'
// Note: Cell transformations are now handled by AgGrid's built-in useBotanicalCellResponseSystem
// No need to import useGridDataTransformers anymore


// AG Grid modules are now registered in the AgGrid abstraction

// Dynamic imports for ag-grid components
let AutocompleteSelectCellEditor: any = null
let CustomEscapeAwareEditor: any = null

if (import.meta.client) {
  try {
    const { AutocompleteSelectCellEditor: ImportedEditor } = await import('ag-grid-autocomplete-editor')
    AutocompleteSelectCellEditor = ImportedEditor
    
    // Create a custom editor that extends AutocompleteSelectCellEditor to handle Escape differently
    CustomEscapeAwareEditor = class extends ImportedEditor {
      private initialValue: any
      
      constructor() {
        super()
      }
      
      // Override the init method to add our Escape key handler
      override init(params: any) {
        // Call the parent's init method first
        super.init(params)
        
        // Store the current value when editing starts
        this.initialValue = params.value
        
        // Add our custom event listener for Escape
        const input = (this as any).eInput
        if (input) {
          input.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
              // Prevent the default Escape behavior
              event.stopPropagation()
              event.preventDefault()
              
              // Set focus back to the cell but keep any changes
              params.api.stopEditing()
              params.api.setFocusedCell(params.rowIndex, params.column)
            }
          })
        }
      }
      
      // Override getValue to ensure we always return a value, even on Escape
      override getValue() {
        // If using an autocomplete component, it may have stored the selected value differently
        if ((this as any).currentItem) {
          return (this as any).currentItem
        }
        
        // Otherwise fallback to the input value
        const input = (this as any).eInput
        return (input?.value) || this.initialValue
      }
    }
  } catch (error) {
    console.warn('Failed to load ag-grid-autocomplete-editor:', error)
  }
}

interface Props {
  data?: Array<Record<string, any>>
  columns?: ColumnDef[]
  height?: string | number
  loading?: boolean
  pagination?: boolean
  pageSize?: number
  showAddButton?: boolean
  checkboxSelection?: boolean
  panel?: Panel
  tool?: Tool
  enablePanelPersistence?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  data: () => [],
  columns: () => [],
  height: '600px',
  loading: false,
  pagination: true,
  pageSize: 100,
  showAddButton: true,
  checkboxSelection: true,
  panel: undefined,
  tool: undefined,
  enablePanelPersistence: true
})

const emit = defineEmits<{
  ready: [api: GridApi]
  cellValueChanged: [event: CellValueChangedEvent]
  addRow: []
  selectionChanged: [selectedRows: any[]]
  deleteSelectedRows: [selectedIndices: number[]]
  dataUpdate: [data: any[]]
}>()

// Setup project store for address components
const projectStore = useProjectStore()
const { selectedProject } = storeToRefs(projectStore)
const address_components = computed(() => selectedProject.value?.address_components)

// Setup autocomplete composable
const { createApiAutocomplete, isLoaded, createBotanicalNameAutocomplete, createCommonNameAutocomplete } = useAgGridAutocomplete()

// Setup PlantGrid composable for data parsing
const { parse } = usePlantGrid()

// Setup panel data system
const { registerPanel, updatePanelData, getPanelData, panelDataState } = usePanelData()

// Setup mitter for panel communication
const mitter = useMitter()

// Cell transformations are now handled automatically by AgGrid's useBotanicalCellResponseSystem
// No need to set up manual transformers

const gridApi = ref<GridApi>()
const selectedRowCount = ref(0)
const agGridRef = ref()

// Use provided data
const gridData = computed(() => props.data)

// Get panel identifier
const panelId = computed(() => {
  return (props.panel as any)?.id || (props.panel as any)?.panel_id || (props.panel as any)?.name
})

// Watch for changes in the shared panel data state for this panel
watch(() => {
  const id = panelId.value
  return id ? panelDataState.value[id] : null
}, (newPanelState) => {
  if (!newPanelState?.data?.output) return
  
  console.log('[PlantGridTwo] Panel data state changed, processing output:', newPanelState.data.output)
  
  try {
    // Process the panel data output
    processAnalysisOutput(newPanelState.data.output, 'panel-data-state')
  } catch (error) {
    console.error('[PlantGridTwo] Error processing panel data state change:', error)
  }
}, { deep: true })

// Helper function to process analysis output consistently
const processAnalysisOutput = (output: any, source: string = 'unknown') => {
  console.log(`[PlantGridTwo] Processing analysis output from ${source}:`, output)
  
  // Handle JSON format from file analysis
  if (output.json && Array.isArray(output.json)) {
    console.log('[PlantGridTwo] Processing JSON data:', output.json.length, 'rows')
    
    // Convert string botanical/common names to object format
    output.json.forEach((row: any) => {
      if (row.botanicalname && typeof row.botanicalname === 'string') {
        row.botanicalname = {
          label: row.botanicalname,
          value: row.botanicalname,
          source: source,
          updatedAt: new Date().toISOString()
        }
      }

      if (row.commonname && typeof row.commonname === 'string') {
        row.commonname = {
          label: row.commonname,
          value: row.commonname,
          source: source,
          updatedAt: new Date().toISOString()
        }
      }
    })
    
    updateGridData([...output.json])
    return true
  }
  
  // Try to parse as analysis result
  const parser = output?.model?.toLowerCase().includes('gemini') ? 'gemini' : 'llamaparse'
  const parsedData = parse(output, parser)
  
  if (parsedData && parsedData.rowData) {
    console.log('[PlantGridTwo] Processing parsed analysis data:', parsedData.rowData.length, 'rows')
    
    // Convert string botanical/common names to object format
    parsedData.rowData.forEach((row: any) => {
      if (row.botanicalname && typeof row.botanicalname === 'string') {
        row.botanicalname = {
          label: row.botanicalname,
          value: row.botanicalname,
          source: source,
          updatedAt: new Date().toISOString()
        }
      }

      if (row.commonname && typeof row.commonname === 'string') {
        row.commonname = {
          label: row.commonname,
          value: row.commonname,
          source: source,
          updatedAt: new Date().toISOString()
        }
      }
    })
    
    // Use the utility function to ensure botanical/common name columns are properly configured
    if (parsedData.columnDefs) {
      parsedData.columnDefs = ensureAutocompleteColumns(parsedData.columnDefs)
    }
    
    updateGridData([...parsedData.rowData])
    return true
  }
  
  return false
}

// Create autocomplete configurations using the composable
const botanicalNameAutocompleteConfig = computed(() => {
  // Extract existing botanical names from current grid data
  const existingPlantNames = gridData.value.map((row: any) => {
    const name = row.botanicalname?.value || row.botanicalname
    return typeof name === 'string' ? name : name?.value
  }).filter(Boolean)
  
  return createBotanicalNameAutocomplete({
    existingNames: existingPlantNames,
    addressComponents: address_components.value || undefined,
    debounceMs: 100, // Back to stable timing like the old version
    minLength: 1
  })
})

// Create common name autocomplete configuration
const commonNameAutocompleteConfig = computed(() => {
  return createCommonNameAutocomplete({
    debounceMs: 100,
    minLength: 1
  })
})

// Enhanced column schema that includes botanicalname and code
const enhancedColumnSchema = computed(() => ({
  ...columnSchema,
  properties: {
    code: {
      type: "string",
      description: "Botanical code of the plant"
    },
    botanicalname: {
      type: "string",
      description: `The scientific name used to accurately identify a plant. Usually in Latin and has at least two parts: the broader "Genus" and the specific "species". Extra terms can be added for subspecies, varieties, or cultivars.`
    },
    ...columnSchema.properties,
    commonname: {
      type: "string",
      description: "This is the vernacular or everyday conversational name for a plant, such as 'Golden Wattle' or 'Lilly Pilly'. These names are often inconsistent and vary by location"
    }
  }
}))

// Autocomplete configurations by field name
const autocompleteConfigs = computed(() => ({
  botanicalname: botanicalNameAutocompleteConfig.value.autocomplete,
  commonname: commonNameAutocompleteConfig.value.autocomplete
}))

// Enhanced schema that includes autocomplete information for botanicalname
const enhancedSchema = computed(() => {
  const baseSchema = {
    ...enhancedColumnSchema.value,
    properties: {
      ...enhancedColumnSchema.value.properties
    }
  }

  return baseSchema
});

// Event handlers
const onGridReady = (api: GridApi) => {
  gridApi.value = api
  emit('ready', api)
}

const onCellValueChanged = async (event: CellValueChangedEvent) => {
  emit('cellValueChanged', event)
  
  // Cell transformations are now handled automatically by AgGrid's useBotanicalCellResponseSystem
  // No need for manual transformation logic here
  console.log('[PlantGridTwo] Cell value changed:', event.colDef?.field, '- AgGrid will handle transformations')
}

const onSelectionChanged = (selectedRows: any[]) => {
  selectedRowCount.value = selectedRows.length
  emit('selectionChanged', selectedRows)
}

// Event handlers for AgGrid component
const handleAddRow = () => {
  emit('addRow')
}

const deleteSelectedRows = (selectedIndices: number[]) => {
  emit('deleteSelectedRows', selectedIndices)
}

// Helper function to update grid data through the AgGrid component
const updateGridData = (newData: any[]) => {
  console.log('[PlantGridTwo] Updating grid data:', newData.length, 'rows')
  
  // Call the AgGrid component's internal data update
  if (agGridRef.value && typeof agGridRef.value.handleDataUpdate === 'function') {
    console.log('[PlantGridTwo] Calling AgGrid handleDataUpdate directly')
    agGridRef.value.handleDataUpdate(newData)
  } else {
    // Fallback to emit for backward compatibility
    console.log('[PlantGridTwo] Falling back to emit for data update')
    emit('dataUpdate', newData)
  }
}

// Track if we're currently updating to prevent loops
const isUpdating = ref(false)

const handleDataUpdate = (newData: any[]) => {
  if (isUpdating.value) {
    console.log('[PlantGridTwo] Skipping handleDataUpdate - already updating')
    return
  }
  
  isUpdating.value = true
  try {
    updateGridData(newData)
  } finally {
    // Reset flag after a delay to ensure call stack clears
    setTimeout(() => {
      isUpdating.value = false
    }, 10)
  }
}

// Enhanced panel events system handlers
const handleIncomingPanelData = (incomingData: { sourcePanelId: string; sourceToolId: string; data: any }) => {
  console.log('[PlantGridTwo] Received panel data:', incomingData)
  
  // Handle direct grid data format (from other grid components)
  if (incomingData.data?.data?.type === 'grid' && incomingData.data?.data?.rowData) {
    console.log('[PlantGridTwo] Processing direct grid data')
    
    // Convert string botanical/common names to object format like PlantGrid does
    incomingData.data.data.rowData.forEach((row: any) => {
      if (row.botanicalname && typeof row.botanicalname === 'string') {
        row.botanicalname = {
          label: row.botanicalname,
          value: row.botanicalname,
          source: 'panel-connection',
          updatedAt: new Date().toISOString()
        }
      }

      if (row.commonname && typeof row.commonname === 'string') {
        row.commonname = {
          label: row.commonname,
          value: row.commonname,
          source: 'panel-connection',
          updatedAt: new Date().toISOString()
        }
      }
    })
    
    console.log('[PlantGridTwo] Updating data with direct grid format')
    updateGridData([...incomingData.data.data.rowData])
    return
  }

  // Handle PDF analysis results using centralized processor
  if (incomingData.data?.data) {
    console.log('[PlantGridTwo] Processing PDF analysis data via centralized processor')
    
    if (processAnalysisOutput(incomingData.data.data, 'panel-connection')) {
      return // Successfully processed
    }
  }
  
  // Handle different types of incoming data by source tool ID
  switch (incomingData.sourceToolId) {
    case 'file-analysis':
      // Handle CSV/JSON file data
      if (incomingData.data?.json && Array.isArray(incomingData.data.json)) {
        console.log('[PlantGridTwo] Processing file analysis data')
        
        // Convert string botanical/common names to object format
        incomingData.data.json.forEach((row: any) => {
          if (row.botanicalname && typeof row.botanicalname === 'string') {
            row.botanicalname = {
              label: row.botanicalname,
              value: row.botanicalname,
              source: 'file-analysis',
              updatedAt: new Date().toISOString()
            }
          }

          if (row.commonname && typeof row.commonname === 'string') {
            row.commonname = {
              label: row.commonname,
              value: row.commonname,
              source: 'file-analysis',
              updatedAt: new Date().toISOString()
            }
          }
        })
        
        updateGridData([...incomingData.data.json])
      }
      break
    case 'plant-database':
      // Handle plant database lookups
      if (incomingData.data?.plantData) {
        console.log('[PlantGridTwo] Processing plant database data')
        // Merge or update existing data with plant database results
      }
      break
    case 'validation-tool':
      // Handle validation results
      if (incomingData.data?.validationResults) {
        console.log('[PlantGridTwo] Processing validation results')
        // Could highlight errors, show suggestions, etc.
      }
      break
    default:
      // Generic grid data handling
      if (incomingData.data?.gridData && Array.isArray(incomingData.data.gridData)) {
        console.log('[PlantGridTwo] Processing generic grid data')
        updateGridData(incomingData.data.gridData)
      }
  }
}

// Public methods (delegated to AgGrid component)
const addRow = (rowData?: Record<string, any>, index?: number) => {
  if (agGridRef.value?.addRow) {
    agGridRef.value.addRow(rowData, index)
  } else {
    emit('addRow')
  }
}

const getSelectedRows = () => {
  return agGridRef.value?.getSelectedRows() || []
}

const clearSelection = () => {
  if (agGridRef.value?.clearSelection) {
    agGridRef.value.clearSelection()
  }
}

// Enhanced panel system methods
const getPanelSystem = () => {
  return agGridRef.value?.panelSystem || null
}

const emitGridData = () => {
  if (agGridRef.value?.panelSystem?.emitGridData) {
    agGridRef.value.panelSystem.emitGridData()
  }
}

const connectToPanel = (panelId: string) => {
  const panelSystem = getPanelSystem()
  if (panelSystem?.panelConnections()?.connect) {
    panelSystem.panelConnections().connect(panelId)
  }
}

// Add external data reception method like PlantGrid
const onDataReceived = (data: any) => {
  console.log('[PlantGridTwo] External onDataReceived called:', data)
  
  // Handle direct grid data format first
  if (data?.data?.type === 'grid' && data?.data?.rowData) {
    console.log('[PlantGridTwo] Processing direct grid data via onDataReceived')
    
    // Convert string botanical/common names to object format
    data.data.rowData.forEach((row: any) => {
      if (row.botanicalname && typeof row.botanicalname === 'string') {
        row.botanicalname = {
          label: row.botanicalname,
          value: row.botanicalname,
          source: 'external',
          updatedAt: new Date().toISOString()
        }
      }

      if (row.commonname && typeof row.commonname === 'string') {
        row.commonname = {
          label: row.commonname,
          value: row.commonname,
          source: 'external',
          updatedAt: new Date().toISOString()
        }
      }
    })
    
    updateGridData([...data.data.rowData])
    return
  }
  
  // Handle file analysis data with direct JSON format
  if (data?.data?.json && Array.isArray(data.data.json)) {
    console.log('[PlantGridTwo] Processing file analysis JSON data')
    updateGridData([...data.data.json])
    return
  }
  
  // Try to parse as analysis result
  const parser = data?.data?.model?.toLowerCase().includes('gemini') ? 'gemini' : 'llamaparse'
  const parsedData = parse(data.data, parser)
  if (parsedData) {
    console.log('[PlantGridTwo] Successfully parsed external data:', {
      rowCount: parsedData.rowData?.length || 0,
      columnCount: parsedData.columnDefs?.length || 0
    })
    
    // Convert string botanical/common names to object format
    parsedData.rowData.forEach((row: any) => {
      if (row.botanicalname && typeof row.botanicalname === 'string') {
        row.botanicalname = {
          label: row.botanicalname,
          value: row.botanicalname,
          source: 'external-analysis',
          updatedAt: new Date().toISOString()
        }
      }

      if (row.commonname && typeof row.commonname === 'string') {
        row.commonname = {
          label: row.commonname,
          value: row.commonname,
          source: 'external-analysis',
          updatedAt: new Date().toISOString()
        }
      }
    })
    
    // Use the utility function to ensure botanical/common name columns are properly configured
    if (parsedData.columnDefs) {
      parsedData.columnDefs = ensureAutocompleteColumns(parsedData.columnDefs)
    }
    
    updateGridData([...parsedData.rowData])
  }
}

// Enhanced grid control methods
const refreshGrid = () => {
  if (agGridRef.value?.refreshData) {
    agGridRef.value.refreshData()
  }
}

const exportToCsv = (filename?: string) => {
  if (agGridRef.value?.exportToCsv) {
    agGridRef.value.exportToCsv(filename)
  }
}

// Expose methods
defineExpose({
  addRow,
  getSelectedRows,
  clearSelection,
  refreshGrid,
  exportToCsv,
  gridApi: () => gridApi.value,
  // Panel system methods
  panelSystem: getPanelSystem,
  emitGridData,
  connectToPanel,
  // External data reception method (matches PlantGrid interface)
  onDataReceived,
  // Access to the underlying AgGrid component
  agGrid: () => agGridRef.value
})



// Register panel with the panel data system
onMounted(() => {
  // Botanical transformers are now auto-registered by AgGrid's useBotanicalCellResponseSystem
  console.log('[PlantGridTwo] AgGrid will handle botanical transformers automatically')
  
  if (!props.panel) {
    console.warn('[PlantGridTwo] No panel provided for registration')
    return
  }
  
  const currentPanelId = panelId.value
  console.log('[PlantGridTwo] Mounting and registering panel:', currentPanelId)

  
  // Register this panel with the panel data system
  if (currentPanelId) {
    const initialPanelData = {
      tool_uid: props.tool?.uid || '',
      output: props.panel.data?.output || {},
      running: false,
      connections: props.panel.data?.connections || [],
      file_id: props.panel.data?.file_id,
      created_at: props.panel.data?.created_at || new Date().toISOString()
    }
    
    console.log('[PlantGridTwo] Registering panel data:', initialPanelData)
    registerPanel(currentPanelId, initialPanelData)
    
    // Update panel data if tool_uid is available
    if (props.tool?.uid) {
      const panelName = (props.panel as any)?.name || (props.panel as any)?.title || currentPanelId
      updatePanelData(panelName, {
        tool_uid: props.tool.uid
      })
      console.log('[PlantGridTwo] Updated panel with tool_uid:', props.tool.uid)
    }
    
    // Check for existing panel data that might have been set before component mounted
    const existingPanelData = getPanelData(currentPanelId)
    if (existingPanelData?.output && Object.keys(existingPanelData.output).length > 0) {
      console.log('[PlantGridTwo] Found existing panel data on mount, processing:', existingPanelData.output)
      processAnalysisOutput(existingPanelData.output, 'existing-panel-data')
    }
  } else {
    console.warn('[PlantGridTwo] No panel identifier available for registration')
  }

  // Listen for chat data requests and emit current grid data
  mitter.listen('chat:request:curate', (request: any) => {
    if (!request?.includePanel?.includes(props.tool?.uid)) return
    
    console.log('[PlantGridTwo] Received chat request, emitting current grid data')
    
    // Emit current grid data with proper structure for chat transformer
    const currentGridData = {
      tool_uid: props.tool?.uid || '',
      panel_id: currentPanelId || '',
      rowData: gridData.value || [],
      columnDefs: props.columns || [],
      timestamp: Date.now()
    }
    
    console.log('[PlantGridTwo] Emitting grid data for chat:', {
      tool_uid: currentGridData.tool_uid,
      rowCount: currentGridData.rowData.length,
      columnCount: currentGridData.columnDefs.length
    })
    
    mitter.emit('chat:response:curate', currentGridData)
  })
  
  // Listen for file analysis completion events to handle simultaneous uploads
  mitter.listen('panel:data:analysis:finished', (event: any) => {
    // Check if this event is for our panel and tool
    if (event.targetPanelId === currentPanelId && event.analysis?.tool_uid === props.tool?.uid) {
      console.log('[PlantGridTwo] Received analysis completion event for our panel:', event)
      
      // Process the analysis result directly
      if (event.analysis?.output) {
        processAnalysisOutput(event.analysis.output, 'analysis-finished-event')
      }
    }
  })
  
  // Listen for direct panel-specific data events (immediate and delayed)
  if (currentPanelId) {
    mitter.listen(`${currentPanelId}:data:received`, (event: any) => {
      console.log('[PlantGridTwo] Received direct panel data event:', event)
      if (event.data && event.tool_uid === props.tool?.uid) {
        processAnalysisOutput(event.data, 'direct-panel-event')
      }
    })
    
    mitter.listen(`${currentPanelId}:data:update`, (event: any) => {
      console.log('[PlantGridTwo] Received delayed panel data update:', event)
      if (event.data && event.tool_uid === props.tool?.uid) {
        processAnalysisOutput(event.data, 'delayed-panel-event')
      }
    })
  }
  
  // Listen for global panel data update events as final fallback
  mitter.listen('panel:data:update:global', (event: any) => {
    if (event.targetPanelId === currentPanelId && event.tool_uid === props.tool?.uid) {
      console.log('[PlantGridTwo] Received global panel data update:', event)
      if (event.data) {
        processAnalysisOutput(event.data, 'global-panel-event')
      }
    }
  })
  
  // Listen for generic panel data received events  
  mitter.listen('panel:data:received', (event: any) => {
    if (event.targetPanelId === currentPanelId && event.tool_uid === props.tool?.uid) {
      console.log('[PlantGridTwo] Received generic panel data event:', event)
      if (event.data) {
        processAnalysisOutput(event.data, 'generic-panel-event')
      }
    }
  })
})

// Add utility function to ensure autocomplete columns are properly configured
const ensureAutocompleteColumns = (columns: any[]): any[] => {
  return columns.map(col => {
    const fieldLower = col.field?.toLowerCase() || ''
    
    // Configure botanical name with autocomplete
    if (fieldLower === 'botanicalname') {
      return {
        ...col,
        cellEditor: CustomEscapeAwareEditor || AutocompleteSelectCellEditor,
        cellEditorParams: botanicalNameAutocompleteConfig.value,
        valueParser: (params: any) => {
          if (!params.newValue) return null
          
          // If we're receiving a properly formatted object already
          if (typeof params.newValue === 'object' && params.newValue.label) {
            return params.newValue
          }
          
          // Handle old value for preservation
          const oldValue = typeof params.oldValue === 'object' ? params.oldValue : null
          
          // If it's a string (direct editing)
          return {
            label: params.newValue || '',
            value: params.newValue || '',
            source: oldValue?.source || 'user-edit',
            updatedAt: new Date().toISOString(),
            genus: oldValue?.genus || '',
            family: oldValue?.family || ''
          }
        },
        valueFormatter: (params: any) => {
          if (typeof params.value === 'object') {
            return params.value?.label || params.value?.value || ''
          }
          return params.value || ''
        }
      }
    }
    
    // Configure common name with autocomplete  
    if (fieldLower === 'commonname') {
      return {
        ...col,
        cellEditor: CustomEscapeAwareEditor || AutocompleteSelectCellEditor,
        cellEditorParams: {
          autocomplete: {
            debounceWaitMs: 100,
            fetch: async (cellEditor: any, text: any, update: any) => {
              try {
                let match = text?.toLowerCase() || ''
                
                // Skip API calls if match is empty or just whitespace
                if (!match || match.trim() === '') {
                  update([])
                  return
                }
                
                // Use our optimized server-side GBIF API endpoint for common names
                const searchData = await $fetch<{results: any[]}>('/api/gbif/autocomplete', {
                  query: {
                    q: match,
                    type: 'vernacularNames',
                    limit: 10
                  },
                  credentials: 'omit',
                })
                
                if (searchData.results?.length > 0) {
                  const vernacularItems = searchData.results
                    .filter(result => result.vernacularName) // Our cached endpoint already filters for vernacular names
                    .map((result: any) => ({
                      value: result.vernacularName,
                      label: result.vernacularName,
                      botanicalName: result.value, // The main species name
                      group: 'Common Name'
                    }))
                  
                  update(vernacularItems)
                  return
                }
                
                update([])
              } catch (error) {
                console.error('Error fetching common name suggestions:', error)
                update([])
              }
            },
            emptyMsg: 'No common names found',
            showOnFocus: false,
            strict: false
          }
        },
        valueParser: (params: any) => {
          const oldValue = params.data[params.column.colId]
          
          // If we're receiving a full object (e.g., from autocomplete)
          if (typeof params.newValue === 'object') {
            return {
              label: params.newValue?.label || params.newValue?.value || '',
              value: params.newValue?.value || params.newValue?.label || '',
              source: params.newValue?.source || oldValue?.source || 'user-edit',
              updatedAt: new Date().toISOString(),
              genus: params.newValue?.genus || oldValue?.genus || '',
              family: params.newValue?.family || oldValue?.family || '',
              botanicalName: params.newValue?.botanicalName || oldValue?.botanicalName || ''
            }
          }
          
          // If we're receiving just a string (direct editing)
          return {
            label: params.newValue || '',
            value: params.newValue || '',
            source: oldValue?.source || 'user-edit',
            updatedAt: new Date().toISOString(),
            genus: oldValue?.genus || '',
            family: oldValue?.family || '',
            botanicalName: oldValue?.botanicalName || ''
          }
        },
        valueFormatter: (params: any) => {
          if (typeof params.value === 'object') {
            return params.value?.label || params.value?.value || ''
          }
          return params.value || ''
        }
      }
    }
    
    return col
  })
}
</script>

<template>
  <AgGrid
    ref="agGridRef"
    :data="gridData"
    :schema="enhancedSchema"
    :columns="props.columns"
    :autocomplete-configs="autocompleteConfigs"
    :height="height"
    :loading="loading"
    :pagination="pagination"
    :page-size="pageSize"
    :show-add-button="showAddButton"
    :checkbox-selection="checkboxSelection"
    :row-selection="checkboxSelection ? undefined : 'multiple'"
    :enable-zoom="true"
    :show-floating-controls="true"
    :auto-focus-first-cell="true"
    :enable-autocomplete="true"
    :panel="panel"
    :tool="tool"
    :enable-panel-persistence="enablePanelPersistence"
    :enable-transformers="true"
    :transformers-config="{
      asyncOperationsDisabled: false,
      debounceMs: 100,
      enableLogging: true
    }"
    :enable-panel-events="true"
    :enable-update-queue="true"
    :enable-mitter-events="true"
    :enable-validation-dropdown="true"
    :get-row-id="(params: any) => params.data._id || params.data.id || `row_${params.node?.rowIndex || Date.now()}`"
    :on-data-received="handleIncomingPanelData"
    @ready="onGridReady"
    @cell-value-changed="onCellValueChanged"
    @selection-changed="onSelectionChanged"
    @add-row="handleAddRow"
    @delete-selected-rows="deleteSelectedRows"
    @data-update="handleDataUpdate"
  />
</template>